import { Container, ContainerSize, Heading, Row, Column, Text, Col, RowCols, Gutter, Offset, AlignItems, AlignSelf, Order, JustifyContent } from '@rebootui';
import CodeSnippet from "../../../components/controls/CodeSnippet";
import CodePreview from "../../../components/controls/CodePreview";
import CodeHighlighter from "../../../components/controls/CodeHighlighter";

type GridPageProps = {};

const GridPage = ({ }: GridPageProps) => {
    return (
        <Container size={ContainerSize.Fluid}>
            <Heading el="h1" className="fw-semibold mb-3 ls-1">
                Grid system
            </Heading>
            <Text el="p" className="mb-4">
                Use our powerful mobile-first flexbox grid to build layouts of all shapes and sizes thanks to a twelve column system, six default responsive tiers, and dozens of predefined classes.
            </Text>

            {/* Example: Equal-width */}
            <Heading el="h2" className="fw-medium fs-3 mt-4 mb-3">
                Equal-width
            </Heading>
            <Text className="mb-4">
                For example, here are two grid layouts that apply to every device and viewport, from <Text el="code">xs</Text> to <Text el="code">xxl</Text>. Add any number of unit-less classes for each breakpoint you need and every column will be the same width.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Container>
                        <Row>
                            <Column>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">Column</div>
                            </Column>
                            <Column>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">Column</div>
                            </Column>
                        </Row>
                        <Row className="mt-3">
                            <Column>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">Column</div>
                            </Column>
                            <Column>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">Column</div>
                            </Column>
                            <Column>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">Column</div>
                            </Column>
                        </Row>
                    </Container>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {equalWidthCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Setting one column width */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Setting one column width
            </Heading>
            <Text className="mb-4">
                Auto-layout for flexbox grid columns also means you can set the width of one column and have the sibling columns automatically resize around it.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Container>
                        <Row>
                            <Column>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">1 of 3</div>
                            </Column>
                            <Column col={Col.Col6}>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">2 of 3 (wider)</div>
                            </Column>
                            <Column>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">3 of 3</div>
                            </Column>
                        </Row>
                    </Container>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {oneColumnWidthCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Responsive classes */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Responsive classes
            </Heading>
            <Text className="mb-4">
                Bootstrap's grid includes six tiers of predefined classes for building complex responsive layouts. Customize the size of your columns on extra small, small, medium, large, or extra large devices however you see fit.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Container>
                        <Row>
                            <Column col={[Col.Col12, Col.ColMd8]}>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">col-12 col-md-8</div>
                            </Column>
                            <Column col={[Col.Col6, Col.ColMd4]}>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">col-6 col-md-4</div>
                            </Column>
                        </Row>
                        <Row className="mt-3">
                            <Column col={[Col.Col6, Col.ColMd4]}>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">col-6 col-md-4</div>
                            </Column>
                            <Column col={[Col.Col6, Col.ColMd4]}>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">col-6 col-md-4</div>
                            </Column>
                            <Column col={[Col.Col6, Col.ColMd4]}>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">col-6 col-md-4</div>
                            </Column>
                        </Row>
                    </Container>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {responsiveCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Row columns */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Row columns
            </Heading>
            <Text className="mb-4">
                Use the responsive <Text el="code">cols</Text> prop on <Text el="code">Row</Text> to quickly set the number of columns that best render your content.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Container>
                        <Row cols={RowCols.RowCols2}>
                            <Column>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">Column</div>
                            </Column>
                            <Column>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">Column</div>
                            </Column>
                            <Column>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">Column</div>
                            </Column>
                            <Column>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">Column</div>
                            </Column>
                        </Row>
                    </Container>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {rowColsCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Gutters */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Gutters
            </Heading>
            <Text className="mb-4">
                Gutters are the padding between your columns, used to responsively space and align content in the Bootstrap grid system. Use <Text el="code">gutter</Text> prop on <Text el="code">Row</Text>.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Container>
                        <Row gutter={Gutter.G3}>
                            <Column col={Col.Col6}>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">Column</div>
                            </Column>
                            <Column col={Col.Col6}>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">Column</div>
                            </Column>
                            <Column col={Col.Col6}>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">Column</div>
                            </Column>
                            <Column col={Col.Col6}>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">Column</div>
                            </Column>
                        </Row>
                    </Container>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {guttersCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Horizontal & vertical gutters
            </Heading>
            <Text className="mb-4">
                <Text el="code">gutter</Text> prop controls both horizontal and vertical gutters. In the example below, we use a smaller gutter width.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Container>
                        <Row gutter={Gutter.G2}>
                            <Column col={Col.Col6}>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">Column</div>
                            </Column>
                            <Column col={Col.Col6}>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">Column</div>
                            </Column>
                            <Column col={Col.Col6}>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">Column</div>
                            </Column>
                            <Column col={Col.Col6}>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">Column</div>
                            </Column>
                        </Row>
                    </Container>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {horizontalVerticalGuttersCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Row columns with gutters
            </Heading>
            <Text className="mb-4">
                Gutter classes can also be added to row columns. In the following example, we use responsive row columns and responsive gutter classes.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Container>
                        <Row cols={[RowCols.RowCols2, RowCols.RowColsLg5]} gutter={[Gutter.G2, Gutter.GLg3]}>
                            <Column>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">Row column</div>
                            </Column>
                            <Column>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">Row column</div>
                            </Column>
                            <Column>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">Row column</div>
                            </Column>
                            <Column>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">Row column</div>
                            </Column>
                            <Column>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">Row column</div>
                            </Column>
                            <Column>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">Row column</div>
                            </Column>
                            <Column>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">Row column</div>
                            </Column>
                            <Column>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">Row column</div>
                            </Column>
                            <Column>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">Row column</div>
                            </Column>
                            <Column>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">Row column</div>
                            </Column>
                        </Row>
                    </Container>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {rowColumnsGuttersCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                No gutters
            </Heading>
            <Text className="mb-4">
                The gutters between columns can be removed with <Text el="code">gutter={'{Gutter.G0}'}</Text>. This removes the negative margins from <Text el="code">Row</Text> and the horizontal padding from all immediate children columns.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Container>
                        <Row gutter={Gutter.G0} className="text-center">
                            <Column col={[Col.Col6, Col.ColMd8]}>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">col-6 col-md-8</div>
                            </Column>
                            <Column col={[Col.Col6, Col.ColMd4]}>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">col-6 col-md-4</div>
                            </Column>
                        </Row>
                    </Container>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {noGuttersCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Alignment */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Vertical alignment
            </Heading>
            <Text className="mb-4">
                Use flexbox alignment utilities to vertically and horizontally align columns.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Container>
                        <div className="d-grid border" style={{ minHeight: '10rem' }}>
                            <Row alignItems={AlignItems.Center} className="h-100">
                                <Column>
                                    <div className="p-3 border border-primary bg-primary bg-opacity-10">Column</div>
                                </Column>
                                <Column>
                                    <div className="p-3 border border-primary bg-primary bg-opacity-10">Column</div>
                                </Column>
                                <Column>
                                    <div className="p-3 border border-primary bg-primary bg-opacity-10">Column</div>
                                </Column>
                            </Row>
                        </div>
                    </Container>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {alignmentCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Individual column alignment
            </Heading>
            <Text className="mb-4">
                Use <Text el="code">alignSelf</Text> prop on individual columns to override the row's alignment.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Container>
                        <div className="d-grid border" style={{ minHeight: '10rem' }}>
                            <Row className="h-100">
                                <Column alignSelf={AlignSelf.Start}>
                                    <div className="p-3 border border-primary bg-primary bg-opacity-10">Aligned to start</div>
                                </Column>
                                <Column alignSelf={AlignSelf.Center}>
                                    <div className="p-3 border border-primary bg-primary bg-opacity-10">Aligned to center</div>
                                </Column>
                                <Column alignSelf={AlignSelf.End}>
                                    <div className="p-3 border border-primary bg-primary bg-opacity-10">Aligned to end</div>
                                </Column>
                            </Row>
                        </div>
                    </Container>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {alignSelfCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Horizontal alignment
            </Heading>
            <Text className="mb-4">
                Use <Text el="code">justifyContent</Text> prop to align columns horizontally.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Container>
                        <Row justifyContent={JustifyContent.Start} className="mb-3">
                            <Column col={Col.Col4}>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">Column 1 of 2</div>
                            </Column>
                            <Column col={Col.Col4}>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">Column 2 of 2</div>
                            </Column>
                        </Row>
                        <Row justifyContent={JustifyContent.Center} className="mb-3">
                            <Column col={Col.Col4}>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">Column 1 of 2</div>
                            </Column>
                            <Column col={Col.Col4}>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">Column 2 of 2</div>
                            </Column>
                        </Row>
                        <Row justifyContent={JustifyContent.End} className="mb-3">
                            <Column col={Col.Col4}>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">Column 1 of 2</div>
                            </Column>
                            <Column col={Col.Col4}>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">Column 2 of 2</div>
                            </Column>
                        </Row>
                        <Row justifyContent={JustifyContent.Around} className="mb-3">
                            <Column col={Col.Col4}>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">Column 1 of 2</div>
                            </Column>
                            <Column col={Col.Col4}>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">Column 2 of 2</div>
                            </Column>
                        </Row>
                        <Row justifyContent={JustifyContent.Between} className="mb-3">
                            <Column col={Col.Col4}>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">Column 1 of 2</div>
                            </Column>
                            <Column col={Col.Col4}>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">Column 2 of 2</div>
                            </Column>
                        </Row>
                        <Row justifyContent={JustifyContent.Evenly}>
                            <Column col={Col.Col4}>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">Column 1 of 2</div>
                            </Column>
                            <Column col={Col.Col4}>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">Column 2 of 2</div>
                            </Column>
                        </Row>
                    </Container>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {horizontalAlignmentCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Reordering */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Reordering
            </Heading>
            <Text className="mb-4">
                Use <Text el="code">order</Text> prop to control the visual order of your columns. You can also use responsive <Text el="code">Order.First</Text> and <Text el="code">Order.Last</Text> classes.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Container>
                        <Row>
                            <Column order={Order.Order3}>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">First in DOM, ordered third</div>
                            </Column>
                            <Column>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">Second in DOM, unordered</div>
                            </Column>
                            <Column order={Order.Order1}>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">Third in DOM, ordered first</div>
                            </Column>
                        </Row>
                    </Container>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {reorderingCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Text className="mb-4 mt-4">
                You can also use <Text el="code">Order.First</Text> and <Text el="code">Order.Last</Text> to reorder without specific numbers.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Container>
                        <Row>
                            <Column order={Order.OrderLast}>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">First in DOM, ordered last</div>
                            </Column>
                            <Column>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">Second in DOM, unordered</div>
                            </Column>
                            <Column order={Order.OrderFirst}>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">Third in DOM, ordered first</div>
                            </Column>
                        </Row>
                    </Container>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {reorderingFirstLastCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Offsetting columns */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Offsetting columns
            </Heading>
            <Text className="mb-4">
                Move columns to the right using <Text el="code">offset</Text> prop. These classes increase the left margin of a column by <Text el="code">*</Text> columns.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Container>
                        <Row>
                            <Column col={Col.ColMd4}>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">col-md-4</div>
                            </Column>
                            <Column col={Col.ColMd4} offset={Offset.OffsetMd4}>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">col-md-4 offset-md-4</div>
                            </Column>
                        </Row>
                        <Row className="mt-3">
                            <Column col={Col.ColMd3} offset={Offset.OffsetMd3}>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">col-md-3 offset-md-3</div>
                            </Column>
                            <Column col={Col.ColMd3} offset={Offset.OffsetMd3}>
                                <div className="p-3 border border-primary bg-primary bg-opacity-10">col-md-3 offset-md-3</div>
                            </Column>
                        </Row>
                    </Container>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {offsetCode}
                </CodeHighlighter>
            </CodeSnippet>

        </Container>
    );
}

export default GridPage;

// Code snippets
const equalWidthCode = `<Container>
    <Row>
        <Column>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">Column</div>
        </Column>
        <Column>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">Column</div>
        </Column>
    </Row>
    <Row className="mt-3">
        <Column>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">Column</div>
        </Column>
        <Column>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">Column</div>
        </Column>
        <Column>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">Column</div>
        </Column>
    </Row>
</Container>`;

const oneColumnWidthCode = `<Container>
    <Row>
        <Column>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">1 of 3</div>
        </Column>
        <Column col={Col.Col6}>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">2 of 3 (wider)</div>
        </Column>
        <Column>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">3 of 3</div>
        </Column>
    </Row>
</Container>`;

const responsiveCode = `<Container>
    <Row>
        <Column col={[Col.Col12, Col.ColMd8]}>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">col-12 col-md-8</div>
        </Column>
        <Column col={[Col.Col6, Col.ColMd4]}>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">col-6 col-md-4</div>
        </Column>
    </Row>
</Container>`;

const rowColsCode = `<Container>
    <Row cols={RowCols.RowCols2}>
        <Column>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">Column</div>
        </Column>
        <Column>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">Column</div>
        </Column>
        <Column>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">Column</div>
        </Column>
        <Column>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">Column</div>
        </Column>
    </Row>
</Container>`;

const guttersCode = `<Container>
    <Row gutter={Gutter.G3}>
        <Column col={Col.Col6}>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">Column</div>
        </Column>
        <Column col={Col.Col6}>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">Column</div>
        </Column>
        <Column col={Col.Col6}>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">Column</div>
        </Column>
        <Column col={Col.Col6}>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">Column</div>
        </Column>
    </Row>
</Container>`;

const alignmentCode = `<Container>
    <Row alignItems={AlignItems.Center}>
        <Column>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">Column</div>
        </Column>
        <Column>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">Column</div>
        </Column>
        <Column>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">Column</div>
        </Column>
    </Row>
</Container>`;

const horizontalVerticalGuttersCode = `<Container>
    <Row gutter={Gutter.G2}>
        <Column col={Col.Col6}>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">Column</div>
        </Column>
        <Column col={Col.Col6}>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">Column</div>
        </Column>
        <Column col={Col.Col6}>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">Column</div>
        </Column>
        <Column col={Col.Col6}>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">Column</div>
        </Column>
    </Row>
</Container>`;

const rowColumnsGuttersCode = `<Container>
    <Row cols={[RowCols.RowCols2, RowCols.RowColsLg5]} gutter={[Gutter.G2, Gutter.GLg3]}>
        <Column>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">Row column</div>
        </Column>
        <Column>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">Row column</div>
        </Column>
        <Column>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">Row column</div>
        </Column>
        <Column>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">Row column</div>
        </Column>
        <Column>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">Row column</div>
        </Column>
        <Column>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">Row column</div>
        </Column>
        <Column>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">Row column</div>
        </Column>
        <Column>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">Row column</div>
        </Column>
        <Column>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">Row column</div>
        </Column>
        <Column>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">Row column</div>
        </Column>
    </Row>
</Container>`;

const noGuttersCode = `<Container>
    <Row gutter={Gutter.G0}>
        <Column col={[Col.Col6, Col.ColMd8]}>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">col-6 col-md-8</div>
        </Column>
        <Column col={[Col.Col6, Col.ColMd4]}>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">col-6 col-md-4</div>
        </Column>
    </Row>
</Container>`;

const horizontalAlignmentCode = `<Container>
    <Row justifyContent={JustifyContent.Start}>
        <Column col={Col.Col4}>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">Column 1 of 2</div>
        </Column>
        <Column col={Col.Col4}>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">Column 2 of 2</div>
        </Column>
    </Row>
    <Row justifyContent={JustifyContent.Center}>
        <Column col={Col.Col4}>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">Column 1 of 2</div>
        </Column>
        <Column col={Col.Col4}>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">Column 2 of 2</div>
        </Column>
    </Row>
    <Row justifyContent={JustifyContent.End}>
        <Column col={Col.Col4}>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">Column 1 of 2</div>
        </Column>
        <Column col={Col.Col4}>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">Column 2 of 2</div>
        </Column>
    </Row>
    <Row justifyContent={JustifyContent.Around}>
        <Column col={Col.Col4}>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">Column 1 of 2</div>
        </Column>
        <Column col={Col.Col4}>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">Column 2 of 2</div>
        </Column>
    </Row>
    <Row justifyContent={JustifyContent.Between}>
        <Column col={Col.Col4}>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">Column 1 of 2</div>
        </Column>
        <Column col={Col.Col4}>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">Column 2 of 2</div>
        </Column>
    </Row>
    <Row justifyContent={JustifyContent.Evenly}>
        <Column col={Col.Col4}>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">Column 1 of 2</div>
        </Column>
        <Column col={Col.Col4}>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">Column 2 of 2</div>
        </Column>
    </Row>
</Container>`;

const alignSelfCode = `<Container>
    <Row>
        <Column alignSelf={AlignSelf.Start}>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">Aligned to start</div>
        </Column>
        <Column alignSelf={AlignSelf.Center}>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">Aligned to center</div>
        </Column>
        <Column alignSelf={AlignSelf.End}>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">Aligned to end</div>
        </Column>
    </Row>
</Container>`;

const reorderingCode = `<Container>
    <Row>
        <Column order={Order.Order3}>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">First in DOM, ordered third</div>
        </Column>
        <Column>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">Second in DOM, unordered</div>
        </Column>
        <Column order={Order.Order1}>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">Third in DOM, ordered first</div>
        </Column>
    </Row>
</Container>`;

const reorderingFirstLastCode = `<Container>
    <Row>
        <Column order={Order.Last}>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">First in DOM, ordered last</div>
        </Column>
        <Column>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">Second in DOM, unordered</div>
        </Column>
        <Column order={Order.First}>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">Third in DOM, ordered first</div>
        </Column>
    </Row>
</Container>`;

const offsetCode = `<Container>
    <Row>
        <Column col={Col.ColMd4}>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">col-md-4</div>
        </Column>
        <Column col={Col.ColMd4} offset={Offset.OffsetMd4}>
            <div className="p-3 border border-primary bg-primary bg-opacity-10">col-md-4 offset-md-4</div>
        </Column>
    </Row>
</Container>`;
